import * as PIXI from 'pixi.js';
import { useCampaignMapStore } from '../stores/campaignMapStore';
import type { CampaignMap, CampaignPin, FogRegion, PartyLocation, PinType } from '@machina/core';
import { getPinStateColor } from '@machina/core';

/**
 * CampaignMapLayer
 *
 * Renders the campaign/world map with:
 * - Campaign map image background
 * - Risk-style fog of war (solid fog with revealed polygon cutouts)
 * - Location pins (clickable markers)
 * - Party marker showing current location
 *
 * Coordinates are stored as percentages (0-100) and converted to pixels at render time.
 */
export class CampaignMapLayer {
  public container: PIXI.Container;

  // Sub-containers for layer ordering
  private mapContainer: PIXI.Container;
  private fogContainer: PIXI.Container;
  private pinsContainer: PIXI.Container;
  private partyContainer: PIXI.Container;

  // Sprites and graphics
  private mapSprite: PIXI.Sprite | null = null;
  private fogGraphics: PIXI.Graphics;
  private fogPatternGraphics: PIXI.Graphics;
  private pinSprites: Map<string, PIXI.Container> = new Map();
  private partyMarker: PIXI.Container | null = null;

  // Selection
  private selectedPinId: string | null = null;

  // State
  private unsubscribe: (() => void) | null = null;
  private canvasWidth = 1000; // Full canvas dimensions
  private canvasHeight = 1000;
  // Actual rendered map bounds (after aspect-ratio-preserving fit)
  private mapBounds = { x: 0, y: 0, width: 1000, height: 1000 };
  // Locked aspect ratio from loaded map image (prevents fog stretching)
  private mapAspectRatio: number | null = null;
  private isGM = true; // Set via setGMMode()
  private showFogAsPlayer = false; // GM preview: see fog as players do
  private editMode = false; // GM edit mode: show all labels, enable drag
  private currentMapUrl: string | null = null; // Track loaded URL to avoid redundant loads
  private loadGeneration = 0; // Increment on each load to discard stale results

  // Callbacks
  public onPinClick: ((pinId: string) => void) | null = null;
  public onPinHover: ((pinId: string | null) => void) | null = null;
  public onMapClick: ((x: number, y: number) => void) | null = null;
  public onPinMoved: ((pinId: string, x: number, y: number) => void) | null = null;
  public onPinToggleVisibility: ((pinId: string, visible: boolean) => void) | null = null;
  public onPartyMarkerMoved: ((x: number, y: number) => void) | null = null;

  constructor() {
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;

    // Create sub-containers in layer order (bottom to top)
    this.mapContainer = new PIXI.Container();
    this.mapContainer.zIndex = 0;

    this.fogContainer = new PIXI.Container();
    this.fogContainer.zIndex = 1;

    this.pinsContainer = new PIXI.Container();
    this.pinsContainer.zIndex = 2;

    this.partyContainer = new PIXI.Container();
    this.partyContainer.zIndex = 3;

    // Create fog graphics
    this.fogGraphics = new PIXI.Graphics();
    this.fogPatternGraphics = new PIXI.Graphics();
    this.fogContainer.addChild(this.fogGraphics);
    this.fogContainer.addChild(this.fogPatternGraphics);

    // Add to main container
    this.container.addChild(this.mapContainer);
    this.container.addChild(this.fogContainer);
    this.container.addChild(this.pinsContainer);
    this.container.addChild(this.partyContainer);

    // Subscribe to store changes
    this.subscribeToStore();
  }

  private subscribeToStore(): void {
    // DISABLED: Subscriptions were causing blinking
    // Only render once on init, use refresh() for manual updates
    const state = useCampaignMapStore.getState();
    this.renderAll(state);
  }

  /**
   * Set canvas dimensions (call when canvas size changes)
   * Note: We do NOT recalculate mapBounds on resize - the map stays fixed
   * to prevent fog/pins/party from moving around during window resize.
   * Map bounds are only set when the map image first loads.
   */
  setMapDimensions(width: number, height: number): void {
    // Only update canvas dimensions on first call (when map hasn't loaded yet)
    // After the map loads, we lock the bounds and don't change them on resize
    if (!this.mapSprite && !this.mapAspectRatio) {
      this.canvasWidth = width;
      this.canvasHeight = height;
      // Default to 4:3 aspect ratio for consistent fog shape
      this.calculateMapBoundsFromAspectRatio(4 / 3);
      const state = useCampaignMapStore.getState();
      this.renderAll(state);
    }
    // If map is already loaded, do nothing - keep everything fixed
  }

  /**
   * Calculate map bounds from a known aspect ratio (used when image not loaded)
   */
  private calculateMapBoundsFromAspectRatio(aspectRatio: number): void {
    const canvasAspect = this.canvasWidth / this.canvasHeight;
    let w: number, h: number;
    if (canvasAspect > aspectRatio) {
      // Canvas is wider than map - fit by height
      h = this.canvasHeight;
      w = h * aspectRatio;
    } else {
      // Canvas is taller than map - fit by width
      w = this.canvasWidth;
      h = w / aspectRatio;
    }
    this.mapBounds = {
      x: (this.canvasWidth - w) / 2,
      y: (this.canvasHeight - h) / 2,
      width: w,
      height: h,
    };
  }

  /**
   * Calculate map bounds to fit image within canvas preserving aspect ratio
   */
  private calculateMapBounds(texture: PIXI.Texture): void {
    const imgW = texture.width;
    const imgH = texture.height;
    // Lock the aspect ratio so fog doesn't stretch on resize
    this.mapAspectRatio = imgW / imgH;
    const scale = Math.min(this.canvasWidth / imgW, this.canvasHeight / imgH);
    const w = imgW * scale;
    const h = imgH * scale;
    this.mapBounds = {
      x: (this.canvasWidth - w) / 2,
      y: (this.canvasHeight - h) / 2,
      width: w,
      height: h,
    };
  }

  /**
   * Set GM mode (affects fog visibility)
   */
  setGMMode(isGM: boolean): void {
    this.isGM = isGM;
    const state = useCampaignMapStore.getState();
    const pins = Array.from(state.pins.values());
    this.renderFog(Array.from(state.regions.values()), pins);
    this.renderPins(pins);
  }

  /**
   * Toggle fog preview (GM sees map as players would)
   */
  setPlayerPreview(preview: boolean): void {
    this.showFogAsPlayer = preview;
    const state = useCampaignMapStore.getState();
    const pins = Array.from(state.pins.values());
    this.renderFog(Array.from(state.regions.values()), pins);
    this.renderPins(pins);
  }

  getPlayerPreview(): boolean {
    return this.showFogAsPlayer;
  }

  /**
   * Toggle edit mode (GM sees all labels, can drag pins)
   */
  setEditMode(editing: boolean): void {
    this.editMode = editing;
    const state = useCampaignMapStore.getState();
    this.renderPins(Array.from(state.pins.values()));
  }

  /**
   * Render everything from store state
   */
  private renderAll(state: ReturnType<typeof useCampaignMapStore.getState>): void {
    const pins = Array.from(state.pins.values());
    this.renderMap(state.activeCampaignMap);
    this.renderFog(Array.from(state.regions.values()), pins);
    this.renderPins(pins);
    this.renderPartyMarker(state.partyLocation);
  }

  // ===========================================================================
  // MAP BACKGROUND
  // ===========================================================================

  private renderMap(map: CampaignMap | null): void {
    console.log('[CampaignMapLayer] renderMap called, map:', map ? { id: map.id, name: map.name, imageUrl: map.imageUrl?.substring(0, 50) } : null);
    if (!map) {
      console.log('[CampaignMapLayer] No map to render, clearing');
      this.clearMapContainer();
      this.currentMapUrl = null;
      return;
    }

    // Skip reload if same URL is already loaded
    if (map.imageUrl === this.currentMapUrl && this.mapSprite) return;

    this.clearMapContainer();
    this.currentMapUrl = map.imageUrl;
    this.loadMapTexture(map.imageUrl);
  }

  private clearMapContainer(): void {
    // Remove ALL children (sprite, placeholder graphics, text, etc.)
    this.mapContainer.removeChildren();
    if (this.mapSprite) {
      this.mapSprite.destroy();
      this.mapSprite = null;
    }
  }

  private async loadMapTexture(url: string): Promise<void> {
    const thisGeneration = ++this.loadGeneration;

    try {
      // Try to load the texture with CORS settings
      const texture = await PIXI.Assets.load<PIXI.Texture>({
        src: url,
        loadParser: 'loadTextures',
        data: { crossOrigin: 'anonymous' },
      });

      // Discard if a newer load was started while we were loading
      if (thisGeneration !== this.loadGeneration) return;

      this.createMapSprite(texture);
      console.log('[CampaignMapLayer] Loaded map texture successfully');

      // Re-render fog, pins, and party marker now that mapBounds are correct
      const state = useCampaignMapStore.getState();
      const pins = Array.from(state.pins.values());
      this.renderFog(Array.from(state.regions.values()), pins);
      this.renderPins(pins);
      this.renderPartyMarker(state.partyLocation);
    } catch (error) {
      if (thisGeneration !== this.loadGeneration) return;

      console.warn('[CampaignMapLayer] Failed to load map texture, using placeholder:', error);
      this.createPlaceholderMap();
    }
  }

  private createMapSprite(texture: PIXI.Texture): void {
    this.mapSprite = new PIXI.Sprite(texture);

    // Calculate aspect-ratio-preserving bounds
    this.calculateMapBounds(texture);

    // Position and scale the sprite to fit within canvas (contain mode)
    this.mapSprite.x = this.mapBounds.x;
    this.mapSprite.y = this.mapBounds.y;
    this.mapSprite.width = this.mapBounds.width;
    this.mapSprite.height = this.mapBounds.height;

    // Make map interactive for click-to-place-pin
    this.mapSprite.eventMode = 'static';
    this.mapSprite.on('pointertap', (event: PIXI.FederatedPointerEvent) => {
      if (this.onMapClick) {
        // Convert pixel position to percentage coordinates relative to map bounds
        const x = ((event.global.x - this.mapBounds.x) / this.mapBounds.width) * 100;
        const y = ((event.global.y - this.mapBounds.y) / this.mapBounds.height) * 100;
        if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
          this.onMapClick(x, y);
        }
      }
    });

    this.mapContainer.addChild(this.mapSprite);
  }

  private createPlaceholderMap(): void {
    // Use full canvas for placeholder
    this.mapBounds = { x: 0, y: 0, width: this.canvasWidth, height: this.canvasHeight };

    const graphics = new PIXI.Graphics();

    // Base parchment color
    graphics.beginFill(0x2a231a);
    graphics.drawRect(0, 0, this.canvasWidth, this.canvasHeight);
    graphics.endFill();

    // Add a subtle grid pattern
    graphics.lineStyle(1, 0x3a332a, 0.3);
    const gridSize = 50;
    for (let x = 0; x <= this.canvasWidth; x += gridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, this.canvasHeight);
    }
    for (let y = 0; y <= this.canvasHeight; y += gridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(this.canvasWidth, y);
    }

    // Add decorative border
    graphics.lineStyle(3, 0xd4af37, 0.6);
    graphics.drawRect(10, 10, this.canvasWidth - 20, this.canvasHeight - 20);
    graphics.lineStyle(1, 0xc0c0c0, 0.4);
    graphics.drawRect(15, 15, this.canvasWidth - 30, this.canvasHeight - 30);

    // Make interactive for click-to-place-pin
    graphics.eventMode = 'static';
    graphics.on('pointertap', (event: PIXI.FederatedPointerEvent) => {
      if (this.onMapClick) {
        const x = ((event.global.x - this.mapBounds.x) / this.mapBounds.width) * 100;
        const y = ((event.global.y - this.mapBounds.y) / this.mapBounds.height) * 100;
        if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
          this.onMapClick(x, y);
        }
      }
    });

    this.mapContainer.addChild(graphics);

    // Add "Map Image Failed to Load" text
    const text = new PIXI.Text('Map image failed to load\n(CORS or invalid URL)', {
      fontFamily: 'Georgia, serif',
      fontSize: 16,
      fill: 0x8b8070,
      align: 'center',
    });
    text.anchor.set(0.5);
    text.x = this.canvasWidth / 2;
    text.y = this.canvasHeight / 2;
    this.mapContainer.addChild(text);
  }

  // ===========================================================================
  // FOG OF WAR
  // ===========================================================================

  // Hex reveal radius in percentage of map width (approx 3-4 hex rings on the Chult map)
  private static HEX_REVEAL_RADIUS_PCT = 6;

  private renderFog(regions: FogRegion[], pins?: CampaignPin[]): void {
    this.fogGraphics.clear();
    this.fogPatternGraphics.clear();

    // Determine which pins are revealed (not hidden)
    const allPins = pins ?? Array.from(useCampaignMapStore.getState().pins.values());
    const revealedPins = allPins.filter((p) => p.state !== 'hidden');

    // If no manual regions AND no revealed pins, skip fog entirely (nothing to contrast)
    if (regions.length === 0 && revealedPins.length === 0) return;

    const effectiveGM = this.isGM && !this.showFogAsPlayer;
    // Player view: near-black, fully opaque — players should NOT be able to
    // read terrain through fog. Previous 0x06140a + alpha 1.0 still let the
    // colored map read through because the dark-green tint blended with the
    // jungle palette. Using near-black kills that contrast entirely.
    // GM view: 0.65 so the GM can see what they're hiding but players at a
    // shared screen still get a clear "this is covered" signal. Previous
    // 0.35 was transparent enough that a player sitting next to the GM
    // could read the whole map.
    const fogAlpha = effectiveGM ? 0.65 : 1.0;
    const fogColor = effectiveGM ? 0x06140a : 0x02060a;

    // Draw fog as an organic interior shape that retreats around discovered pins
    const fogPolygon = this.getInteriorFogPolygon(revealedPins);
    this.fogGraphics.beginFill(fogColor, fogAlpha);
    this.fogGraphics.drawPolygon(fogPolygon);
    this.fogGraphics.endFill();

    // Cut circular reveal holes around each discovered pin
    const revealRadius = CampaignMapLayer.HEX_REVEAL_RADIUS_PCT;
    for (const pin of revealedPins) {
      this.cutCircularFogHole(pin.x, pin.y, revealRadius);
    }

    if (regions.length > 0) {
      // Manual mode: use defined polygon regions as additional reveals
      for (const region of regions) {
        if (region.isRevealed) {
          this.cutPolygonFogHole(region.vertices);
        }
      }
    }

    // Draw jungle pattern overlay on top of fog
    this.renderJunglePattern(fogPolygon, effectiveGM);
  }

  /**
   * Cut a polygon hole in the fog (manual region mode)
   */
  private cutPolygonFogHole(vertices: Array<{ x: number; y: number }>): void {
    if (vertices.length < 3) return;

    const points = vertices.flatMap((v) => [
      this.mapBounds.x + (v.x / 100) * this.mapBounds.width,
      this.mapBounds.y + (v.y / 100) * this.mapBounds.height,
    ]);

    this.fogGraphics.beginHole();
    this.fogGraphics.drawPolygon(points);
    this.fogGraphics.endHole();
  }

  /**
   * Cut a soft circular hole in the fog around a revealed pin.
   * Coordinates are in percentage of map dimensions.
   */
  private cutCircularFogHole(pctX: number, pctY: number, radiusPct: number): void {
    const cx = this.mapBounds.x + (pctX / 100) * this.mapBounds.width;
    const cy = this.mapBounds.y + (pctY / 100) * this.mapBounds.height;
    // Use the larger dimension so the hole isn't squished
    const radius = (radiusPct / 100) * Math.max(this.mapBounds.width, this.mapBounds.height);

    // Generate organic circle with sine noise for natural edge
    const segments = 48;
    const points: number[] = [];
    for (let i = 0; i < segments; i++) {
      const angle = (Math.PI * 2 / segments) * i;
      const noise = 1 + Math.sin(angle * 3) * 0.08 + Math.cos(angle * 5) * 0.05;
      points.push(cx + radius * noise * Math.cos(angle));
      points.push(cy + radius * noise * Math.sin(angle));
    }

    this.fogGraphics.beginHole();
    this.fogGraphics.drawPolygon(points);
    this.fogGraphics.endHole();
  }

  /**
   * Draw a procedural jungle pattern (vines, fronds, leaves) over the fog area.
   * Draws directly onto fogPatternGraphics which is masked to the fog polygon.
   * Uses seeded pseudo-random to keep the pattern stable across re-renders.
   */
  private renderJunglePattern(fogPolygon: number[], isGMView: boolean): void {
    const g = this.fogPatternGraphics;
    const patternAlpha = isGMView ? 0.06 : 0.35;

    // Remove old mask if any
    if (g.mask) {
      const oldMask = g.mask as PIXI.Graphics;
      if (oldMask.parent) oldMask.parent.removeChild(oldMask);
      oldMask.destroy();
      g.mask = null;
    }

    // Mask the pattern to the fog polygon
    const mask = new PIXI.Graphics();
    mask.beginFill(0xffffff);
    mask.drawPolygon(fogPolygon);
    mask.endFill();
    g.mask = mask;
    this.fogContainer.addChild(mask);

    // Seeded pseudo-random for stable pattern
    const seed = 42;
    let rng = seed;
    const rand = () => {
      rng = (rng * 16807 + 0) % 2147483647;
      return (rng & 0x7fffffff) / 0x7fffffff;
    };

    const { x: bx, y: by, width: bw, height: bh } = this.mapBounds;

    // --- Layer 1: Curving vines ---
    const vineCount = 30;
    for (let i = 0; i < vineCount; i++) {
      const startX = bx + rand() * bw;
      const startY = by + rand() * bh;
      const angle = rand() * Math.PI * 2;
      const vineLen = 60 + rand() * 120;
      const segments = 8 + Math.floor(rand() * 6);

      // Darker greens for vine lines
      const vineColors = [0x0d2b0d, 0x132e13, 0x0a200a, 0x1a3a1a];
      const color = vineColors[Math.floor(rand() * vineColors.length)];

      g.lineStyle(1 + rand() * 1.5, color, patternAlpha * (0.6 + rand() * 0.4));
      g.moveTo(startX, startY);

      let cx = startX;
      let cy = startY;
      let a = angle;
      for (let s = 0; s < segments; s++) {
        a += (rand() - 0.5) * 1.2; // gentle curve
        const stepLen = vineLen / segments;
        cx += Math.cos(a) * stepLen;
        cy += Math.sin(a) * stepLen;
        g.lineTo(cx, cy);

        // Small leaf at some vine joints
        if (rand() > 0.55) {
          this.drawLeaf(g, cx, cy, a + (rand() - 0.5) * 1.5, 4 + rand() * 8, color, patternAlpha * 0.7);
        }
      }
      g.lineStyle(0);
    }

    // --- Layer 2: Scattered fern fronds ---
    const frondCount = 20;
    for (let i = 0; i < frondCount; i++) {
      const fx = bx + rand() * bw;
      const fy = by + rand() * bh;
      const fAngle = rand() * Math.PI * 2;
      const fLen = 30 + rand() * 60;
      const leaflets = 5 + Math.floor(rand() * 5);
      const color = rand() > 0.5 ? 0x1a3a1a : 0x0d2b0d;

      // Central stem
      g.lineStyle(1.2, color, patternAlpha * 0.5);
      g.moveTo(fx, fy);
      const endX = fx + Math.cos(fAngle) * fLen;
      const endY = fy + Math.sin(fAngle) * fLen;
      g.lineTo(endX, endY);
      g.lineStyle(0);

      // Leaflets along stem
      for (let l = 0; l < leaflets; l++) {
        const t = (l + 1) / (leaflets + 1);
        const lx = fx + (endX - fx) * t;
        const ly = fy + (endY - fy) * t;
        const side = l % 2 === 0 ? 1 : -1;
        const leafAngle = fAngle + side * (0.5 + rand() * 0.4);
        const leafSize = (3 + rand() * 5) * (1 - t * 0.4);
        this.drawLeaf(g, lx, ly, leafAngle, leafSize, color, patternAlpha * 0.6);
      }
    }

    // --- Layer 3: Large scattered leaves ---
    const bigLeafCount = 15;
    for (let i = 0; i < bigLeafCount; i++) {
      const lx = bx + rand() * bw;
      const ly = by + rand() * bh;
      const la = rand() * Math.PI * 2;
      const lSize = 10 + rand() * 16;
      const color = rand() > 0.5 ? 0x132e13 : 0x1a3a1a;
      this.drawLeaf(g, lx, ly, la, lSize, color, patternAlpha * 0.5);
    }
  }

  /**
   * Draw a single stylized leaf shape (elongated ellipse with midrib).
   */
  private drawLeaf(
    g: PIXI.Graphics,
    x: number, y: number,
    angle: number, size: number,
    color: number, alpha: number,
  ): void {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const tipX = x + cos * size;
    const tipY = y + sin * size;
    const perpX = -sin * size * 0.3;
    const perpY = cos * size * 0.3;
    const midX = x + cos * size * 0.5;
    const midY = y + sin * size * 0.5;

    g.beginFill(color, alpha);
    g.moveTo(x, y);
    g.quadraticCurveTo(midX + perpX, midY + perpY, tipX, tipY);
    g.quadraticCurveTo(midX - perpX, midY - perpY, x, y);
    g.endFill();

    // Midrib line
    g.lineStyle(0.5, color, alpha * 0.8);
    g.moveTo(x, y);
    g.lineTo(tipX, tipY);
    g.lineStyle(0);
  }

  /**
   * Generate an organic polygon covering the map interior, leaving the coast visible.
   * Uses sine-based noise for natural-looking edges rather than a harsh rectangle.
   */
  private getInteriorFogPolygon(revealedPins: CampaignPin[]): number[] {
    const cx = 50; // center in percentage
    const cy = 54; // shifted south — more northern coast visible
    const baseRX = 34; // horizontal radius — covers Sanrach Mountains east
    const baseRY = 32; // vertical radius
    const segments = 128; // high resolution for smooth curves
    const influenceRadius = CampaignMapLayer.HEX_REVEAL_RADIUS_PCT * 5; // very wide, gentle influence

    // Generate base polygon vertices in percentage space
    const pctPoints: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < segments; i++) {
      const angle = (Math.PI * 2 / segments) * i;
      // Layered sine noise for organic coastline shape
      const noise = Math.sin(angle * 3) * 3.0
                   + Math.cos(angle * 5) * 2.0
                   + Math.sin(angle * 7 + 1) * 1.5
                   + Math.cos(angle * 2 + 0.5) * 2.0;
      const rx = baseRX + noise;
      const ry = baseRY + noise;
      pctPoints.push({
        x: cx + rx * Math.cos(angle),
        y: cy + ry * Math.sin(angle),
      });
    }

    // Push fog vertices away from discovered pins (fog retreats from known areas)
    for (const pt of pctPoints) {
      for (const pin of revealedPins) {
        const dx = pt.x - pin.x;
        const dy = pt.y - pin.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < influenceRadius && dist > 0) {
          // Cosine falloff for very smooth, gradual transition
          const t = dist / influenceRadius;
          const strength = (1 + Math.cos(t * Math.PI)) / 2; // cosine ease: 1 at center, 0 at edge
          pt.x += (cx - pt.x) * strength * 0.35;
          pt.y += (cy - pt.y) * strength * 0.35;
        }
      }
    }

    // Multiple smoothing passes for very soft edges
    let smoothed = pctPoints;
    for (let pass = 0; pass < 3; pass++) {
      const src = smoothed;
      smoothed = src.map((pt, i) => {
        const prev = src[(i - 1 + segments) % segments];
        const next = src[(i + 1) % segments];
        return {
          x: prev.x * 0.25 + pt.x * 0.5 + next.x * 0.25,
          y: prev.y * 0.25 + pt.y * 0.5 + next.y * 0.25,
        };
      });
    }

    // Convert to pixel coordinates
    const points: number[] = [];
    for (const pt of smoothed) {
      points.push(this.mapBounds.x + (pt.x / 100) * this.mapBounds.width);
      points.push(this.mapBounds.y + (pt.y / 100) * this.mapBounds.height);
    }
    return points;
  }


  // ===========================================================================
  // PINS
  // ===========================================================================

  private renderPins(pins: CampaignPin[]): void {
    // Clear existing pins
    for (const [, sprite] of this.pinSprites) {
      this.pinsContainer.removeChild(sprite);
      sprite.destroy({ children: true });
    }
    this.pinSprites.clear();

    // Filter pins based on visibility
    // When GM is previewing player view, hide hidden pins
    const effectiveGM = this.isGM && !this.showFogAsPlayer;
    const visiblePins = effectiveGM ? pins : pins.filter((p) => p.state !== 'hidden');

    console.log(`[CampaignMapLayer] Rendering ${visiblePins.length}/${pins.length} pins (isGM=${this.isGM}), mapBounds:`, this.mapBounds);

    // Render each pin
    for (const pin of visiblePins) {
      const pinContainer = this.createPinSprite(pin);
      this.pinsContainer.addChild(pinContainer);
      this.pinSprites.set(pin.id, pinContainer);
    }
  }

  private createPinSprite(pin: CampaignPin): PIXI.Container {
    const container = new PIXI.Container();

    // Position (convert percentage to pixels relative to map bounds)
    container.x = this.mapBounds.x + (pin.x / 100) * this.mapBounds.width;
    container.y = this.mapBounds.y + (pin.y / 100) * this.mapBounds.height;

    // Create pin icon (circle with colored fill)
    const pinSize = 20;
    const pinGraphics = new PIXI.Graphics();

    // Determine colors based on state
    const fillColor = this.getPinFillColor(pin.state);
    const effectiveGM = this.isGM && !this.showFogAsPlayer;
    const borderColor = effectiveGM && pin.state === 'hidden' ? 0x666666 : 0xffffff;
    const alpha = pin.state === 'hidden' ? 0.6 : 1.0;

    // Draw pin marker (circle with drop shadow effect)
    pinGraphics.lineStyle(2, borderColor, alpha);
    pinGraphics.beginFill(fillColor, alpha);
    pinGraphics.drawCircle(0, 0, pinSize / 2);
    pinGraphics.endFill();

    // Add icon based on type (simple shape for now)
    this.drawPinIcon(pinGraphics, pin.iconType, pinSize / 2 - 4);

    container.addChild(pinGraphics);

    // Add label on hover
    const label = new PIXI.Text(pin.name, {
      fontFamily: 'Arial',
      fontSize: 12,
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 2,
    });
    label.anchor.set(0.5, 0);
    label.y = pinSize / 2 + 4;
    label.visible = this.editMode; // Show all labels in edit mode
    container.addChild(label);

    // Make interactive
    container.eventMode = 'static';
    container.cursor = this.editMode ? 'grab' : 'pointer';

    container.on('pointerover', () => {
      label.visible = true;
      if (!this.editMode) pinGraphics.scale.set(1.2);
      this.onPinHover?.(pin.id);
    });

    container.on('pointerout', () => {
      if (!dragging) {
        const isSelected = this.selectedPinId === pin.id;
        label.visible = this.editMode || isSelected;
        if (!this.editMode && !isSelected) pinGraphics.scale.set(1.0);
      }
      this.onPinHover?.(null);
    });

    // Drag-to-reposition (GM only)
    let dragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragOffset = { x: 0, y: 0 };

    const onDragMove = (event: PIXI.FederatedPointerEvent) => {
      if (!dragging) return;
      const local = event.getLocalPosition(this.pinsContainer);
      container.x = local.x - dragOffset.x;
      container.y = local.y - dragOffset.y;
    };

    const onDragEnd = () => {
      if (!dragging) return;
      dragging = false;
      container.alpha = 1.0;
      label.visible = false;
      pinGraphics.scale.set(1.0);

      // Convert final pixel position back to percentage
      const newXPct = ((container.x - this.mapBounds.x) / this.mapBounds.width) * 100;
      const newYPct = ((container.y - this.mapBounds.y) / this.mapBounds.height) * 100;

      // Clamp to map bounds
      const clampedX = Math.max(0, Math.min(100, newXPct));
      const clampedY = Math.max(0, Math.min(100, newYPct));

      // Only fire callback if actually moved (not just a click)
      const dx = container.x - dragStartX;
      const dy = container.y - dragStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.onPinMoved?.(pin.id, clampedX, clampedY);
      }

      // Remove global listeners
      this.container.off('pointermove', onDragMove);
      this.container.off('pointerup', onDragEnd);
      this.container.off('pointerupoutside', onDragEnd);
    };

    container.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      // Always track start position for click detection
      dragStartX = container.x;
      dragStartY = container.y;

      if (!this.editMode) return;

      dragging = true;
      container.alpha = 0.7;
      container.cursor = 'grabbing';

      const local = event.getLocalPosition(this.pinsContainer);
      dragOffset.x = local.x - container.x;
      dragOffset.y = local.y - container.y;

      event.stopPropagation();

      // Listen on parent container for move/up (so dragging works outside pin bounds)
      this.container.eventMode = 'static';
      this.container.on('pointermove', onDragMove);
      this.container.on('pointerup', onDragEnd);
      this.container.on('pointerupoutside', onDragEnd);
    });

    container.on('pointertap', (event: PIXI.FederatedPointerEvent) => {
      // Only fire click if we didn't drag
      const dx = container.x - dragStartX;
      const dy = container.y - dragStartY;
      console.log('[CampaignMapLayer] pointertap on pin:', pin.name, 'editMode:', this.editMode, 'dragDelta:', dx, dy);
      if (Math.abs(dx) <= 3 && Math.abs(dy) <= 3) {
        event.stopPropagation();
        if (this.editMode) {
          // In edit mode: toggle visibility (hidden ↔ discovered)
          const isVisible = pin.state !== 'hidden';
          this.onPinToggleVisibility?.(pin.id, !isVisible);
        } else {
          this.onPinClick?.(pin.id);
        }
      }
    });

    return container;
  }

  private getPinFillColor(state: CampaignPin['state']): number {
    const colorStr = getPinStateColor(state);
    // Convert hex string to number
    return parseInt(colorStr.replace('#', ''), 16);
  }

  private drawPinIcon(graphics: PIXI.Graphics, iconType: PinType, radius: number): void {
    graphics.lineStyle(1, 0xffffff, 0.8);

    // Simple icon shapes based on type
    switch (iconType) {
      case 'town':
      case 'city':
      case 'village':
        // House shape
        graphics.moveTo(-radius / 2, radius / 4);
        graphics.lineTo(0, -radius / 3);
        graphics.lineTo(radius / 2, radius / 4);
        break;
      case 'dungeon':
      case 'cave':
        // Skull-ish shape (two dots and curve)
        graphics.drawCircle(-radius / 4, -radius / 6, 2);
        graphics.drawCircle(radius / 4, -radius / 6, 2);
        break;
      case 'quest':
        // Exclamation mark
        graphics.moveTo(0, -radius / 3);
        graphics.lineTo(0, 0);
        graphics.drawCircle(0, radius / 4, 1.5);
        break;
      case 'encounter':
        // Crossed swords (X)
        graphics.moveTo(-radius / 3, -radius / 3);
        graphics.lineTo(radius / 3, radius / 3);
        graphics.moveTo(radius / 3, -radius / 3);
        graphics.lineTo(-radius / 3, radius / 3);
        break;
      default:
        // Default: dot
        graphics.beginFill(0xffffff, 0.8);
        graphics.drawCircle(0, 0, 2);
        graphics.endFill();
    }
  }

  // ===========================================================================
  // PARTY MARKER
  // ===========================================================================

  private renderPartyMarker(location: PartyLocation | null): void {
    // Clear existing marker
    if (this.partyMarker) {
      this.partyContainer.removeChild(this.partyMarker);
      this.partyMarker.destroy({ children: true });
      this.partyMarker = null;
    }

    if (!location) return;

    // Get position (convert percentage to pixels relative to map bounds)
    let x: number, y: number;
    if (location.currentPinId) {
      const pin = useCampaignMapStore.getState().pins.get(location.currentPinId);
      if (pin) {
        x = this.mapBounds.x + (pin.x / 100) * this.mapBounds.width;
        y = this.mapBounds.y + (pin.y / 100) * this.mapBounds.height;
      } else {
        return;
      }
    } else if (location.x !== undefined && location.y !== undefined) {
      x = this.mapBounds.x + (location.x / 100) * this.mapBounds.width;
      y = this.mapBounds.y + (location.y / 100) * this.mapBounds.height;
    } else {
      return;
    }

    // Create party marker (animated ring)
    this.partyMarker = new PIXI.Container();
    this.partyMarker.x = x;
    this.partyMarker.y = y;

    const markerGraphics = new PIXI.Graphics();
    markerGraphics.lineStyle(3, 0xf59e0b, 1); // Amber color
    markerGraphics.drawCircle(0, 0, 16);
    markerGraphics.lineStyle(2, 0xffffff, 0.8);
    markerGraphics.drawCircle(0, 0, 20);

    this.partyMarker.addChild(markerGraphics);
    this.partyContainer.addChild(this.partyMarker);

    // GM-only: make party marker draggable
    if (!this.isGM) return;

    this.partyMarker.eventMode = 'static';
    this.partyMarker.cursor = 'grab';

    let dragging = false;
    let dragOff = { x: 0, y: 0 };
    const marker = this.partyMarker;

    const onDragMove = (event: PIXI.FederatedPointerEvent) => {
      if (!dragging) return;
      const local = event.getLocalPosition(this.partyContainer);
      marker.x = local.x - dragOff.x;
      marker.y = local.y - dragOff.y;
    };

    const onDragEnd = () => {
      if (!dragging) return;
      dragging = false;
      marker.cursor = 'grab';
      marker.alpha = 1.0;

      // Convert pixel position to percentage
      const pctX = ((marker.x - this.mapBounds.x) / this.mapBounds.width) * 100;
      const pctY = ((marker.y - this.mapBounds.y) / this.mapBounds.height) * 100;
      const clampedX = Math.max(0, Math.min(100, pctX));
      const clampedY = Math.max(0, Math.min(100, pctY));

      this.onPartyMarkerMoved?.(clampedX, clampedY);

      this.container.off('pointermove', onDragMove);
      this.container.off('pointerup', onDragEnd);
      this.container.off('pointerupoutside', onDragEnd);
    };

    this.partyMarker.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
      dragging = true;
      marker.cursor = 'grabbing';
      marker.alpha = 0.7;

      const local = event.getLocalPosition(this.partyContainer);
      dragOff.x = local.x - marker.x;
      dragOff.y = local.y - marker.y;

      event.stopPropagation();

      this.container.eventMode = 'static';
      this.container.on('pointermove', onDragMove);
      this.container.on('pointerup', onDragEnd);
      this.container.on('pointerupoutside', onDragEnd);
    });
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Select a pin — keeps it highlighted with gold ring and label visible.
   * Pass null to deselect.
   */
  selectPin(pinId: string | null): void {
    const prevId = this.selectedPinId;
    this.selectedPinId = pinId;

    // Reset previously selected pin
    if (prevId && prevId !== pinId) {
      const prev = this.pinSprites.get(prevId);
      if (prev) {
        const g = prev.getChildAt(0) as PIXI.Graphics;
        const label = prev.getChildAt(1) as PIXI.Text;
        // Remove selection ring (child index 2 if it exists)
        if (prev.children.length > 2) {
          prev.removeChildAt(2).destroy();
        }
        g.scale.set(1.0);
        label.visible = this.editMode;
      }
    }

    // Apply selection to new pin
    if (pinId) {
      const curr = this.pinSprites.get(pinId);
      if (curr) {
        const g = curr.getChildAt(0) as PIXI.Graphics;
        const label = curr.getChildAt(1) as PIXI.Text;
        g.scale.set(1.2);
        label.visible = true;
        // Add gold selection ring if not already present
        if (curr.children.length <= 2) {
          const ring = new PIXI.Graphics();
          ring.lineStyle(2.5, 0xd4af37, 1);
          ring.drawCircle(0, 0, 15);
          curr.addChildAt(ring, 2);
        }
      }
    }
  }

  /**
   * Force refresh from current store state
   */
  refresh(): void {
    const state = useCampaignMapStore.getState();
    this.renderAll(state);
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.unsubscribe?.();

    if (this.mapSprite) {
      this.mapSprite.destroy();
    }

    this.fogGraphics.destroy();
    if (this.fogPatternGraphics.mask) {
      const m = this.fogPatternGraphics.mask as PIXI.Graphics;
      if (m.parent) m.parent.removeChild(m);
      m.destroy();
    }
    this.fogPatternGraphics.destroy();

    for (const sprite of this.pinSprites.values()) {
      sprite.destroy({ children: true });
    }

    if (this.partyMarker) {
      this.partyMarker.destroy({ children: true });
    }

    this.container.destroy({ children: true });
  }
}
